import { SiteModelGeoref, SiteModelLevel, SiteModelRoom } from './site-model.types';

/**
 * Géoréférencement d'un modèle de site (GLB d'export_model.py, ADR-017) : passage entre les
 * positions de l'API (lat / lon / alt) et le repère du plan, et matrice de placement du GLB sur
 * la carte MapLibre. Fonctions pures, sans three.js ni MapLibre (testées sans navigateur).
 *
 * Repères :
 * - plan : x, y à l'horizontale, z vers le haut, en mètres ;
 * - three.js (contenu du GLB) : X = x, Y = z, Z = −y ;
 * - Mercator MapLibre : x vers l'est, y vers le sud, z vers le haut, en unités du monde [0 ; 1].
 */

/** Point dans le repère du plan (Z vers le haut), en mètres. */
export interface ModelPoint {
  x: number;
  y: number;
  z: number;
}

export interface GeoPoint {
  lat: number;
  lon: number;
  alt: number;
}

const D = Math.PI / 180;

/** Lambert-93 (EPSG:2154, conique conforme sécante, GRS80). Constantes IGN. */
const L93 = (() => {
  const a = 6378137;
  const f = 1 / 298.257222101;
  const e = Math.sqrt(2 * f - f * f);
  const m = (p: number) => Math.cos(p) / Math.sqrt(1 - (e * Math.sin(p)) ** 2);
  const t = (p: number) =>
    Math.tan(Math.PI / 4 - p / 2) / Math.pow((1 - e * Math.sin(p)) / (1 + e * Math.sin(p)), e / 2);
  const p1 = 44 * D;
  const p2 = 49 * D;
  const n = (Math.log(m(p1)) - Math.log(m(p2))) / (Math.log(t(p1)) - Math.log(t(p2)));
  const F = m(p1) / (n * Math.pow(t(p1), n));
  const r0 = a * F * Math.pow(t(46.5 * D), n);
  return { a, e, n, F, r0 };
})();

/** @returns [easting, northing] en mètres */
export function wgs84ToLambert93(lat: number, lon: number): [number, number] {
  const { a, e, n, F, r0 } = L93;
  const t =
    Math.tan(Math.PI / 4 - (lat * D) / 2) / Math.pow((1 - e * Math.sin(lat * D)) / (1 + e * Math.sin(lat * D)), e / 2);
  const r = a * F * Math.pow(t, n);
  const th = n * (lon * D - 3 * D);
  return [700000 + r * Math.sin(th), 6600000 + r0 - r * Math.cos(th)];
}

/** @returns [latitude, longitude] en degrés */
export function lambert93ToWgs84(easting: number, northing: number): [number, number] {
  const { a, e, n, F, r0 } = L93;
  const dx = easting - 700000;
  const dy = r0 - (northing - 6600000);
  const r = Math.hypot(dx, dy);
  const tt = Math.pow(r / (a * F), 1 / n);
  const th = Math.atan2(dx, dy);
  let phi = Math.PI / 2 - 2 * Math.atan(tt);
  for (let i = 0; i < 10; i++) {
    phi = Math.PI / 2 - 2 * Math.atan(tt * Math.pow((1 - e * Math.sin(phi)) / (1 + e * Math.sin(phi)), e / 2));
  }
  return [phi / D, th / n / D + 3];
}

/** Position de l'API → repère du plan. */
export function geoToModel(g: SiteModelGeoref, p: GeoPoint): ModelPoint {
  const [E, N] = wgs84ToLambert93(p.lat, p.lon);
  const o = g.lasOrigin;
  const d = [E - o[0], N - o[1], p.alt - o[2]];
  const v = g.rotationMatrix.map(r => r[0] * d[0] + r[1] * d[1] + r[2] * d[2]);
  return { x: v[0], y: v[1], z: v[2] - g.zOffset };
}

/** Repère du plan → position lat / lon / alt (R est une rotation : son inverse est sa transposée). */
export function modelToGeo(g: SiteModelGeoref, p: ModelPoint): GeoPoint {
  const R = g.rotationMatrix;
  const v = [p.x, p.y, p.z + g.zOffset];
  const d = [0, 1, 2].map(j => R[0][j] * v[0] + R[1][j] * v[1] + R[2][j] * v[2]);
  const [lat, lon] = lambert93ToWgs84(d[0] + g.lasOrigin[0], d[1] + g.lasOrigin[1]);
  return { lat, lon, alt: d[2] + g.lasOrigin[2] };
}

/** Marge sous un plancher : un point à 0,4 m sous le plancher est encore à cet étage. */
const LEVEL_MARGIN_M = 0.4;

/** Étage d'un point : dernier niveau dont le plancher est sous lui (à la marge près). */
export function levelOf(levels: SiteModelLevel[], z: number): SiteModelLevel {
  let best = levels[0];
  for (const l of levels) {
    if (z >= l.z - LEVEL_MARGIN_M) {
      best = l;
    }
  }
  return best;
}

/** Étage et plus petite pièce de cet étage qui contient le point (null si aucune). */
export function roomOf(
  levels: SiteModelLevel[],
  rooms: SiteModelRoom[],
  p: ModelPoint
): { level: SiteModelLevel; room: SiteModelRoom | null } {
  const level = levelOf(levels, p.z);
  const hits = rooms.filter(
    r => r.level === level.id && p.x >= r.rect[0] && p.x <= r.rect[2] && p.y >= r.rect[1] && p.y <= r.rect[3]
  );
  hits.sort((a, b) => (a.areaM2 ?? Infinity) - (b.areaM2 ?? Infinity));
  return { level, room: hits[0] ?? null };
}

/**
 * Hauteur (repère du plan) au-dessus de laquelle le bâtiment est coupé pour montrer un étage :
 * 2,5 m au-dessus de son plancher, sans dépasser 0,25 m sous l'étage suivant ; `all` = pas de coupe.
 */
export function cutHeightFor(levels: SiteModelLevel[], levelId: string | 'all'): number {
  if (levelId === 'all') {
    return Number.POSITIVE_INFINITY;
  }
  const i = levels.findIndex(l => l.id === levelId);
  if (i < 0) {
    return Number.POSITIVE_INFINITY;
  }
  const next = levels[i + 1];
  return next ? Math.min(levels[i].z + 2.5, next.z - 0.25) : levels[i].z + 2.4;
}

/** Altitude du plancher le plus bas : posée sur le plan de la carte (z Mercator = 0). */
export function groundAltitude(g: SiteModelGeoref, levels: SiteModelLevel[]): number {
  return modelToGeo(g, { x: 0, y: 0, z: levels[0]?.z ?? 0 }).alt;
}

/** Coordonnées Mercator MapLibre d'un point lat / lon (z = 0). */
export type ToMercator = (lon: number, lat: number) => { x: number; y: number };

/**
 * Matrice 4 × 4 (colonnes, comme three.js et MapLibre) qui envoie le contenu three.js du GLB dans
 * les coordonnées Mercator de MapLibre.
 *
 * three → plan (x = X, y = −Z, z = Y) → décalage Lambert-93 `d = Rᵀ·(plan + [0, 0, zOffset])`
 * → Mercator : `J·[dE, dN]` (différences finies à l'origine : convergence des méridiens et échelle
 * de la projection comprises), `dAlt × meterInMercator`, plancher le plus bas à z = 0.
 */
export function modelToMercatorMatrix(
  g: SiteModelGeoref,
  levels: SiteModelLevel[],
  toMercator: ToMercator,
  meterInMercator: number
): number[] {
  const o = g.lasOrigin;
  const R = g.rotationMatrix;
  const mc = (E: number, N: number) => {
    const [lat, lon] = lambert93ToWgs84(E, N);
    return toMercator(lon, lat);
  };
  const c0 = mc(o[0], o[1]);
  const cE = mc(o[0] + 1, o[1]);
  const cN = mc(o[0], o[1] + 1);
  const s = meterInMercator;
  // K : décalage Lambert-93 (dE, dN, dAlt) → décalage Mercator.
  const K = [
    [cE.x - c0.x, cN.x - c0.x, 0],
    [cE.y - c0.y, cN.y - c0.y, 0],
    [0, 0, s],
  ];
  const Rt = [0, 1, 2].map(i => [0, 1, 2].map(j => R[j][i]));
  // P : three → plan.
  const P = [
    [1, 0, 0],
    [0, 0, -1],
    [0, 1, 0],
  ];
  const mul = (A: number[][], B: number[][]) =>
    A.map(row => [0, 1, 2].map(j => row[0] * B[0][j] + row[1] * B[1][j] + row[2] * B[2][j]));
  const A = mul(mul(K, Rt), P);
  const tz = Rt.map(row => row[2] * g.zOffset);
  const t = K.map(row => row[0] * tz[0] + row[1] * tz[1] + row[2] * tz[2]);
  t[0] += c0.x;
  t[1] += c0.y;
  t[2] += (o[2] - groundAltitude(g, levels)) * s;
  return [
    A[0][0],
    A[1][0],
    A[2][0],
    0,
    A[0][1],
    A[1][1],
    A[2][1],
    0,
    A[0][2],
    A[1][2],
    A[2][2],
    0,
    t[0],
    t[1],
    t[2],
    1,
  ];
}

/** Point du plan → position three.js (contenu du GLB). */
export function modelToThree(p: ModelPoint): [number, number, number] {
  return [p.x, p.z, -p.y];
}
